// DEMO DATA — NOT a customer registry, and not wired to any API.
//
// The Krapf demo shows a trip-intake panel where each detail is tagged with where it came from:
// "From email", "Looked up", but also "Account", "Past trips" and "Account rule". The first two are
// derived from the live flow turn (TurnResult.extracted / hookResults). The last three claim the
// system already KNOWS this customer — account on file, where they usually load, how they are billed —
// and no such customer-memory store exists in Movorra today.
//
// Rather than fake those badges in the UI, the knowledge lives here as an explicit, inspectable
// fixture. Every row it feeds is rendered with the <DemoBadge/> primitive ("This element uses
// hardcoded data, not a live API"), so a viewer is never misled about what is real.
//
// Replacing this with something real means: a customer/account record keyed by email domain, plus a
// trip history query. Keep the shape and the lookup function; drop the data.
//
// TO RUN THE DEMO: add the address you will actually send from. Lookup is by exact address first,
// then by domain. An unknown sender simply returns null and the panel shows no account badges — the
// flow still works, it just has nothing extra to say about them.

const ACCOUNTS = [
  {
    match: { domains: ['wcasd.net'], addresses: [] },
    groupName: 'West Chester Area School District',
    accountName: 'WCASD Athletics & Activities',
    // Surfaces as: Pickup [Past trips]
    usualPickup: {
      location: 'Henderson High School',
      city: 'West Chester',
      state: 'PA',
      note: 'Loads at the Montgomery Ave bus loop. Last 6 district trips.',
    },
    // Surfaces as: Billing [Account rule]
    billing: {
      accountType: 'tax-exempt',
      label: 'WCASD Athletics & Activities',
      rule: 'Tax-exempt. A PO number is required on every trip.',
      requiresPo: true,
    },
    tripsOnFile: 6,
  },
  {
    match: { domains: ['shanahan.org'], addresses: [] },
    groupName: 'Bishop Shanahan High School',
    accountName: 'Bishop Shanahan Athletics',
    usualPickup: {
      location: 'Bishop Shanahan High School',
      city: 'Downingtown',
      state: 'PA',
      note: 'Team trips load at the gym entrance. Last 4 trips.',
    },
    billing: {
      accountType: 'standard',
      label: 'Bishop Shanahan Athletics',
      rule: 'Billed to the athletics account. No PO required.',
      requiresPo: false,
    },
    tripsOnFile: 4,
  },
];

/** The account on file for a sender address, or null when we have never dealt with them. */
export function accountForSender(emailAddress) {
  const addr = String(emailAddress || '').trim().toLowerCase();
  if (!addr) return null;
  const domain = addr.includes('@') ? addr.split('@').pop() : '';
  const exact = ACCOUNTS.find((a) => (a.match.addresses || []).some((x) => x.toLowerCase() === addr));
  if (exact) return exact;
  return ACCOUNTS.find((a) => (a.match.domains || []).some((d) => d.toLowerCase() === domain)) || null;
}

/**
 * Account knowledge expressed as flow field values, so the intake panel can show a detail we already
 * know before the customer has said it.
 *
 * Deliberately NOT pushed into the flow session: these are our records, not the customer's words, and
 * the standing rule is that a customer correction always wins. Anything here is shown as account
 * knowledge and is overridden the moment the live turn carries a value for the same field.
 */
export function accountPrefill(account) {
  if (!account) return {};
  const out = {};
  if (account.groupName) out.groupName = { value: account.groupName, source: 'Account' };
  if (account.usualPickup) {
    out.pickupLocation = { value: account.usualPickup.location, source: 'Past trips', note: account.usualPickup.note };
    out.pickupCity = { value: account.usualPickup.city, source: 'Past trips' };
    out.pickupState = { value: account.usualPickup.state, source: 'Past trips' };
  }
  if (account.billing) {
    out.billingAccount = { value: account.billing.label, source: 'Account rule', note: account.billing.rule };
    out.billingAccountType = { value: account.billing.accountType, source: 'Account rule' };
  }
  return out;
}
